import { forwardRef, useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Tooltip } from '@mui/material';

import { useStyles } from './status.styles';
import { IStatusProps } from './status.models';
import { OrderStatus, OrderType } from '../../../../../../graphql/order/models';
import infoIcon from '../../../../../../assets/images/icons/info/info.svg';
import infoIconPurple from '../../../../../../assets/images/icons/info/info-purple.svg';
import closeIcon from '../../../../../../assets/images/icons/close.svg';
import { getStatusDate } from '../utils/get-status-date';
import { ExternalLinks } from '../../../../../../constants';
import {
  getOrderIconByStatus,
  getOrderMessageByStatus,
  getOrderStatusTranslationByType,
} from '../../../../../../utils/order';
import { cx } from '../../../../../../utils';

const TOOLTIP_POPPER_PROPS = {
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [0, -7],
      },
    },
  ],
};

const ARROW_PROPS = { sx: { color: '#fff' } };

const TOOLTIP_COMPONENTS_PROPS_CANCELED = {
  tooltip: {
    sx: {
      backgroundColor: '#fff',
      width: '15.5rem',
      height: '10.5rem',
      boxShadow: '0 2px 14px rgba(0, 0, 0, 0.15)',
    },
  },
  arrow: ARROW_PROPS,
};

const TOOLTIP_SHARED_PROPS = {
  tooltip: {
    sx: {
      backgroundColor: '#fff',
      width: '12.9375rem',
      height: '6.5rem',
      borderRadius: '0.5rem',
      boxShadow: '0 2px 14px rgba(0, 0, 0, 0.15)',
      padding: 0,
      gap: '0.625rem',
      opacity: 1,
    },
  },
  arrow: ARROW_PROPS,
};

const TOOLTIP_COMPONENTS_PROPS = {
  ...TOOLTIP_SHARED_PROPS,
  tooltip: {
    ...TOOLTIP_SHARED_PROPS.tooltip,
    sx: {
      ...TOOLTIP_SHARED_PROPS.tooltip.sx,
      height: '8.5rem',
    },
  },
};

const TooltipButton = forwardRef<HTMLButtonElement,
{ icon: string } & React.ButtonHTMLAttributes<HTMLButtonElement>
>(
  ({ icon, ...buttonProps }, ref): JSX.Element => {
    const classes = useStyles();
    const intl = useIntl();

    return (
      <button
        ref={ref}
        type="button"
        {...buttonProps}
        className={cx(classes.infoButton, classes.icoButton)}
        aria-label={intl.formatMessage({
          id: 'order.status.tooltip.aria.label.open',
        })}
      >
        <img src={icon} alt="" width={16} height={16} />
      </button>
    );
  },
);

export const Status = ({
  status,
  deliveryDate,
  oldDeliveryDate,
  purchaseDate,
  lastModifiedDateTime,
  orderType,
  pickupMaxDate,
}: IStatusProps): JSX.Element => {
  const classes = useStyles();
  const intl = useIntl();

  const [canceledAutoTooltipOpen, setCanceledAutoTooltipOpen] = useState(false);
  const [dateChangedTooltipOpen, setDateChangedTooltipOpen] = useState(false);
  const [delayedTooltipOpen, setDelayedTooltipOpen] = useState(false);

  const isDelivery = orderType === OrderType.delivery;
  const isFulfilled = status === OrderStatus.fulfilled;

  const messageId = getOrderMessageByStatus(status, orderType);

  const isCanceledAutomatically = status === OrderStatus.canceledAutomatically;

  const isCanceled =
    status === OrderStatus.canceled ||
    status === OrderStatus.canceledAutomatically ||
    status === OrderStatus.canceledByPatient;

  const isSmallIcon = isFulfilled || isCanceled;

  const isDeliveryActiveStatus = isDelivery && !isCanceled && !isFulfilled;

  const hasDateChanged =
    isDelivery &&
    status === OrderStatus.processing &&
    oldDeliveryDate !== null;

  const isDeliveryDelayed =
    isDelivery
    && status === OrderStatus.externalOrderCreated
    && !!deliveryDate
    && new Date(deliveryDate) < new Date(new Date().toDateString());

  const statusDate = useMemo(() => {
    if (isDelivery && status === OrderStatus.confirmed && pickupMaxDate) {
      return pickupMaxDate;
    }

    return getStatusDate(status, {
      deliveryDate,
      purchaseDate,
      lastModifiedDateTime,
    });
  }, [status, deliveryDate, purchaseDate, lastModifiedDateTime, isDelivery, pickupMaxDate]);

  const handleCloseCanceledAutoTooltip = (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.stopPropagation();
    setCanceledAutoTooltipOpen(false);
  };

  return (
    <div className={classes.statusContainer}>
      <div className={classes.statusImgContainer}>
        <img
          src={getOrderIconByStatus(status, orderType)}
          className={isSmallIcon ? classes.statusImgSmall : classes.statusImg}
          alt=""
        />
      </div>
      <h3 className={classes.title}>
        <FormattedMessage id={getOrderStatusTranslationByType(status, orderType, true)} />
        {isFulfilled && statusDate && (
          <>
            {' '}
            <FormattedDate
              value={statusDate}
              day="2-digit"
              month="2-digit"
              year="numeric"
            />
          </>
        )}
      </h3>
      {!isFulfilled && (
        <div className={cx(classes.statusTextContainer, (isCanceledAutomatically && !!statusDate) && classes.statusTextContainerRow)}>
          {!isDeliveryDelayed && (
            <p className={cx(
              classes.text,
              { [classes.emphasisText]: isDelivery },
            )}>
              <FormattedMessage id={messageId} />
              {hasDateChanged && deliveryDate ? (
                <>
                  {' '}
                  <FormattedDate
                    value={deliveryDate}
                    day="2-digit"
                    month="2-digit"
                    year="numeric"
                  />
                  {' '}
                  <Tooltip
                    open={dateChangedTooltipOpen}
                    onOpen={() => setDateChangedTooltipOpen(true)}
                    onClose={() => setDateChangedTooltipOpen(false)}
                    placement="bottom"
                    arrow
                    enterTouchDelay={0}
                    leaveTouchDelay={3000}
                    PopperProps={TOOLTIP_POPPER_PROPS}
                    componentsProps={TOOLTIP_SHARED_PROPS}
                    title={
                      <div className={classes.popoverContentDelayed}>
                        <p className={cx(classes.popoverText, classes.popoverTextFirst)}>
                          <FormattedMessage
                            id="order.status.tooltip.dateChanged.text"
                            values={{
                              date: (
                                <FormattedDate
                                  value={deliveryDate}
                                  day="2-digit"
                                  month="2-digit"
                                  year="numeric"
                                />
                              ),
                            }}
                          />
                        </p>
                        <p className={classes.popoverText}>
                          <FormattedMessage id="order.status.tooltip.dateChanged.label" />
                        </p>
                      </div>
                    }
                  >
                    <TooltipButton icon={infoIconPurple} />
                  </Tooltip>
                </>
              ) : (
                statusDate && (
                  <>
                    {' '}
                    <FormattedDate
                      value={statusDate}
                      day="2-digit"
                      month="2-digit"
                      year="numeric"
                    />
                  </>
                )
              )}
            </p>
          )}
          {isDeliveryDelayed && (
            <p className={classes.text}>
              <span className={classes.emphasisText}>
                <FormattedMessage id="order.status.delivery.delayed" />
              </span>
              {' '}
              <Tooltip
                open={delayedTooltipOpen}
                onOpen={() => setDelayedTooltipOpen(true)}
                onClose={() => setDelayedTooltipOpen(false)}
                placement="bottom"
                arrow
                enterTouchDelay={0}
                leaveTouchDelay={3000}
                PopperProps={TOOLTIP_POPPER_PROPS}
                componentsProps={TOOLTIP_COMPONENTS_PROPS}
                title={
                  <div className={classes.popoverContentDelayed}>
                    <p className={cx(classes.popoverText, classes.popoverTextFirst)}>
                      <FormattedMessage id="order.status.tooltip.dateChanged.label" />
                    </p>
                    <p className={classes.popoverText}>
                      <FormattedMessage id="order.status.tooltip.delayed.text" />
                    </p>
                    <a
                      href={`tel:${ExternalLinks.phoneNumber}`}
                      className={classes.linkUnderline}
                    >
                      <FormattedMessage id="common.phoneNumber.support" />
                    </a>
                  </div>
                }
              >
                <TooltipButton icon={infoIconPurple} />
              </Tooltip>
            </p>
          )}
          {isDeliveryActiveStatus && (
            <p className={classes.text}>
              <FormattedMessage id="order.status.delivery.payment" />
            </p>
          )}
          {isCanceledAutomatically && statusDate && (
            <Tooltip
              open={canceledAutoTooltipOpen}
              onOpen={() => setCanceledAutoTooltipOpen(true)}
              onClose={() => setCanceledAutoTooltipOpen(false)}
              placement="bottom"
              arrow
              enterTouchDelay={0}
              leaveTouchDelay={3000}
              PopperProps={TOOLTIP_POPPER_PROPS}
              componentsProps={TOOLTIP_COMPONENTS_PROPS_CANCELED}
              title={
                <div className={classes.popoverContent}>
                  <button
                    type="button"
                    data-testid="status-tooltip-close"
                    onClick={handleCloseCanceledAutoTooltip}
                    className={cx(classes.closeButton, classes.icoButton)}
                    aria-label={intl.formatMessage({
                      id: 'appointmentCancel.closeButton.label',
                    })}
                  >
                    <img src={closeIcon} alt="" width={11} height={11} />
                  </button>
                  <p className={cx(classes.popoverText, classes.popoverTextFirst)}>
                    <FormattedMessage id="order.status.tooltip.canceledAutomatically.title" />
                  </p>
                  <p className={classes.popoverText}>
                    <FormattedMessage id="order.status.tooltip.canceledAutomatically.text" />
                  </p>
                  <a
                    href={`tel:${ExternalLinks.phoneNumber}`}
                    className={classes.linkUnderline}
                  >
                    <FormattedMessage id="common.phoneNumber.support" />
                  </a>
                </div>
              }
            >
              <TooltipButton icon={infoIcon} />
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
};
